// apps/office/src/layouts/OfficeLayout.jsx
import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  FileText,
  DollarSign,
  ShoppingCart,
  MessageCircle,
  Home,
  AlertCircle,
  BarChart3,
  LogOut,
  Menu,
  X,
  Tag,
  Receipt,
  ExternalLink,
  Briefcase,
  Wallet,
  Settings,
  Layers,
  FolderOpen,
  Mail,
  ChevronDown,
  ChevronRight,
  Building2,
  PaintBucket,
  Sparkles,
  Package,
  Percent,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import pb from '../lib/pocketbaseClient';

const LOGO_URL = 'https://files.housedesigns99.com/api/files/f5eolh49d789mqi/rwth7jtsmlcst1n/house_designs99_logo_fwjsybt4i7.png';

const OfficeLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    console.log('🔍 OfficeLayout – user:', user);
    console.log('🔍 Role:', user?.role);
  }, [user]);

  /* Auto-open submenu if current path matches */
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/office/interior-projects') || path.includes('/office/interior-')) {
      setOpenSubmenus((prev) => ({ ...prev, quotations: true }));
    }
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const { data: cartClientsCount = 0 } = useQuery({
    queryKey: ['cartClientsCount'],
    queryFn: async () => {
      try {
        const carts = await pb.collection('carts').getFullList({});
        let count = 0;
        for (const cart of carts) {
          const items = await pb.collection('cart_items').getFullList({
            filter: `cartId = "${cart.id}"`,
            limit: 1,
          });
          if (items.length > 0) count++;
        }
        return count;
      } catch {
        return 0;
      }
    },
    refetchInterval: 30000,
    staleTime: 10000,
  });

  const userRole = user?.role?.toLowerCase();
  const isAdmin = userRole === 'admin' || userRole === 'super_admin' || userRole === 'manager';
  const isSales = userRole === 'sales';

  const isActive = (path) => {
    const fullPath = location.pathname;
    if (path === 'dashboard') return fullPath === '/office' || fullPath === '/office/dashboard';
    return fullPath.startsWith(`/office/${path}`);
  };

  /* ─── Toggle submenu ─── */
  const toggleSubmenu = (key) => {
    setOpenSubmenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ─── Menu groups ──────────────────────────────────────────────
  const menuGroups = [
    {
      title: 'Office Work',
      icon: Briefcase,
      iconColor: 'text-emerald-400',
      highlighted: true,
      items: [
        {
          path: 'clients',
          label: 'Clients',
          icon: Users,
          color: 'text-cyan-400',
        },
        {
          path: 'quotations',
          label: 'Quotations',
          icon: FileText,
          color: 'text-amber-400',
          submenu: [
            {
              path: 'quotations',
              label: 'Architecture',
              icon: Building2,
              color: 'text-blue-400',
              description: 'Building & planning quotations',
            },
            {
              path: 'interior-projects',
              label: 'Interior',
              icon: PaintBucket,
              color: 'text-emerald-400',
              description: 'Interior project quotations',
            },
          ],
        },
        {
          path: 'invoices',
          label: 'Invoices',
          icon: DollarSign,
          color: 'text-green-400',
        },
        {
          path: 'payments-dashboard',
          label: 'Payments',
          icon: Wallet,
          color: 'text-lime-400',
        },
        {
          path: 'expenses',
          label: 'Expenses',
          icon: Receipt,
          color: 'text-emerald-400',
        },
        {
          path: 'profitability',
          label: 'Profitability',
          icon: Percent,
          color: 'text-fuchsia-400',
        },
      ],
    },
    {
      title: 'Operations',
      icon: Layers,
      iconColor: 'text-purple-400',
      items: [
        { path: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, color: 'text-blue-400' },
        { path: 'tasks', label: 'Tasks', icon: AlertCircle, color: 'text-rose-400' },
        { path: 'projects', label: 'Projects', icon: FolderOpen, color: 'text-indigo-400' },
        { path: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'text-green-400' },
        { path: 'properties', label: 'Properties', icon: Home, color: 'text-orange-400' },
        { path: 'abandoned', label: 'Abandoned Carts', icon: AlertCircle, color: 'text-red-400' },
        { path: 'reports', label: 'Reports', icon: BarChart3, color: 'text-indigo-400' },
        { path: 'contact-inquiries', label: 'Contact Inquiries', icon: Mail, color: 'text-blue-400' },
      ],
    },
    {
      title: 'Admin',
      icon: Settings,
      iconColor: 'text-pink-400',
      items: [
        ...(isAdmin ? [
          { path: 'price-list', label: 'Price List', icon: Tag, color: 'text-yellow-400' },
          { path: 'interior-products', label: 'Interior Products', icon: Package, color: 'text-emerald-400' },
        ] : []),
        { path: 'cart-overview', label: 'Cart Overview', icon: ShoppingCart, color: 'text-orange-400', badge: cartClientsCount },
        ...(isSales || isAdmin ? [{ path: 'planning-prices', label: 'Planning Prices', icon: Receipt, color: 'text-teal-400' }] : []),
        { path: 'approvals', label: 'Approvals', icon: AlertCircle, color: 'text-pink-400' },
      ],
      condition: isAdmin || isSales,
    },
  ];

  const filteredGroups = menuGroups.filter((group) => group.condition !== false);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* ─── Top Bar ─── */}
      <header className="bg-white shadow-sm px-6 py-3 flex items-center justify-between border-b border-slate-200 sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Logo" className="w-8 h-8 rounded-lg object-contain" />
          <span className="text-xl font-bold text-slate-800">
            Office <span className="text-amber-500">CRM</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-700">{user?.name || 'User'}</span>
          <span className="text-xs bg-slate-200 text-slate-600 px-2.5 py-1 rounded-full font-medium">
            {user?.role || 'guest'}
          </span>
          <button
            onClick={handleLogout}
            className="text-slate-400 hover:text-red-500 transition text-sm px-2 py-1 hover:bg-red-50 rounded-lg"
          >
            ⏻
          </button>
        </div>
      </header>

      {/* ─── Body ─── */}
      <div className="flex flex-1 overflow-hidden">
        <button
          className="lg:hidden fixed bottom-4 left-4 z-50 p-2 bg-white rounded-md shadow"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-6 w-6" />
        </button>

        {/* ─── Sidebar ─── */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-40 w-72 bg-gradient-to-b from-slate-900 via-gray-900 to-black text-white shadow-2xl transform transition-transform duration-300 lg:translate-x-0 flex flex-col',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          {/* Brand */}
          <div className="flex justify-between items-center p-5 border-b border-white/10 shrink-0">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Logo" className="w-8 h-8 rounded-lg object-contain" />
              <h1 className="text-xl font-bold tracking-tight">Office CRM</h1>
            </div>
            <button className="lg:hidden p-1 text-white/70 hover:text-white" onClick={() => setSidebarOpen(false)}>
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* User info */}
          <div className="px-4 py-3 border-b border-white/10 shrink-0">
            <p className="text-xs font-medium text-indigo-300 uppercase tracking-wider">Logged in as</p>
            <p className="font-semibold text-white">
              {user?.name || 'User'}{' '}
              <span className="text-indigo-300 text-sm">({user?.role || 'guest'})</span>
            </p>
          </div>

          {/* Scrollable navigation */}
          <div className="flex-1 overflow-y-auto px-3 py-4">
            {filteredGroups.map((group, idx) => {
              const isHighlighted = group.highlighted;
              return (
                <div
                  key={idx}
                  className={cn(
                    'mb-4 rounded-xl overflow-hidden transition-all',
                    isHighlighted &&
                      'bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-emerald-500/5 border-2 border-emerald-500/40 shadow-lg shadow-emerald-500/10'
                  )}
                >
                  {/* Group header */}
                  <div
                    className={cn(
                      'flex items-center gap-2 px-3 py-2',
                      isHighlighted
                        ? 'bg-gradient-to-r from-emerald-500/30 to-teal-500/20 border-b border-emerald-500/30'
                        : 'py-1.5'
                    )}
                  >
                    <group.icon
                      className={cn(
                        'w-4 h-4',
                        isHighlighted ? 'text-emerald-300' : group.iconColor || 'text-indigo-300'
                      )}
                    />
                    <span
                      className={cn(
                        'text-xs font-semibold uppercase tracking-wider',
                        isHighlighted ? 'text-emerald-200' : 'text-indigo-300'
                      )}
                    >
                      {group.title}
                    </span>
                    {isHighlighted && (
                      <Sparkles className="w-3 h-3 text-emerald-300 ml-auto opacity-80" />
                    )}
                  </div>

                  {/* Items */}
                  <div className="space-y-0.5 p-1.5">
                    {group.items.map((item) => {
                      if (item.condition === false) return null;
                      const hasSubmenu = item.submenu && item.submenu.length > 0;
                      const isSubmenuOpen = openSubmenus[item.path] || false;
                      const parentActive = isActive(item.path);

                      return (
                        <div key={item.path}>
                          {/* Parent item */}
                          {hasSubmenu ? (
                            <button
                              onClick={() => toggleSubmenu(item.path)}
                              className={cn(
                                'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                                parentActive
                                  ? 'bg-amber-500/20 text-amber-300 shadow-lg shadow-amber-500/10 border-r-4 border-amber-400'
                                  : 'text-indigo-200 hover:bg-white/10 hover:text-white'
                              )}
                            >
                              <span className="flex items-center gap-3">
                                <item.icon className={`w-5 h-5 ${item.color || 'text-indigo-300'} flex-shrink-0`} />
                                <span className="whitespace-nowrap">{item.label}</span>
                              </span>
                              {isSubmenuOpen ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </button>
                          ) : (
                            <Link
                              to={item.path}
                              className={cn(
                                'flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                                parentActive
                                  ? 'bg-amber-500/20 text-amber-300 shadow-lg shadow-amber-500/10 border-r-4 border-amber-400'
                                  : 'text-indigo-200 hover:bg-white/10 hover:text-white'
                              )}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <span className="flex items-center gap-3">
                                <item.icon className={`w-5 h-5 ${item.color || 'text-indigo-300'} flex-shrink-0`} />
                                <span className="whitespace-nowrap">{item.label}</span>
                              </span>
                              {item.badge > 0 && (
                                <Badge className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                                  {item.badge}
                                </Badge>
                              )}
                            </Link>
                          )}

                          {/* Submenu items */}
                          {hasSubmenu && isSubmenuOpen && (
                            <div className="ml-3 mt-1 pl-3 border-l-2 border-white/10 space-y-0.5">
                              {item.submenu.map((sub) => {
                                const subActive =
                                  location.pathname === `/office/${sub.path}` ||
                                  location.pathname.startsWith(`/office/${sub.path}/`);
                                return (
                                  <Link
                                    key={sub.path}
                                    to={sub.path}
                                    className={cn(
                                      'flex items-start gap-2 px-3 py-2 rounded-lg text-sm transition-all',
                                      subActive
                                        ? 'bg-emerald-500/20 text-emerald-200 border-l-2 border-emerald-400'
                                        : 'text-indigo-200/80 hover:bg-white/5 hover:text-white'
                                    )}
                                    onClick={() => setSidebarOpen(false)}
                                  >
                                    <sub.icon
                                      className={cn(
                                        'w-4 h-4 mt-0.5 flex-shrink-0',
                                        subActive ? 'text-emerald-300' : sub.color || 'text-indigo-300'
                                      )}
                                    />
                                    <span className="flex flex-col">
                                      <span className="font-medium whitespace-nowrap">{sub.label}</span>
                                      {sub.description && (
                                        <span className="text-[10px] text-slate-400 whitespace-nowrap">
                                          {sub.description}
                                        </span>
                                      )}
                                    </span>
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom links */}
          <div className="border-t border-white/10 p-3 shrink-0 space-y-2">
            <a
              href="http://localhost:3000"
              className="flex items-center gap-3 px-3 py-2 text-indigo-200 hover:bg-white/10 hover:text-white rounded-lg transition"
            >
              <ExternalLink className="w-5 h-5 flex-shrink-0" />
              <span className="whitespace-nowrap">Homepage</span>
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2 w-full text-red-300 hover:bg-red-500/20 hover:text-red-200 rounded-lg transition"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              <span className="whitespace-nowrap">Logout</span>
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto p-6 lg:ml-72">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default OfficeLayout;
